import { Icon } from '@starter/ui';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { JobShelf } from '../jobs/shelf.tsx';
import { authClient } from '../lib/auth.ts';
import { VideoDoors } from '../video/doors.tsx';
import { TemplateShelf } from '../video/shelf.tsx';

/**
 * «Video ads» — the doors into the video composer, and the works behind them.
 *
 * **The doors are rows, not markup.** Which kinds of work move, what each one
 * needs before it can be ordered, and why one of them cannot be ordered here
 * are all answers in `job_kinds` — so a flow that becomes possible becomes
 * possible by a migration, and this screen finds out on its own
 * (`docs/CATALOGS.md`).
 *
 * Two of the three are shown and cannot be pressed, which is deliberate and the
 * same rule the rail follows: a door that is silently absent is a worse lie
 * than one that says what it is waiting for. What they are waiting for is a
 * presenter, and the presenters in the original are photographs of real people
 * — the one thing in this rebuild that cannot be made from measurements
 * (`prd/DIVERGENCE.md`).
 */
function VideoAds() {
  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-wrap items-start gap-4 py-2">
        <h1
          className="min-w-0 flex-1 font-display text-[28px] leading-[34px] font-bold tracking-[-0.7px] text-ink"
          data-testid="video-ads-title"
        >
          Video ads
        </h1>
      </header>

      <section className="flex flex-col gap-4">
        <h2 className="text-[20px] leading-6 font-bold text-ink">Start a video</h2>
        <VideoDoors testIdPrefix="door-" />
      </section>

      {/*
        The shelf of ready-made adverts, which is what this page mostly is in
        the original: thirty-six of ours against six hundred and sixty-eight of
        theirs, and no footage on either — ours because we have none to use.
      */}
      <section className="flex flex-col gap-4">
        <h2 className="text-[20px] leading-6 font-bold text-ink">Start from a ready-made one</h2>
        <TemplateShelf />
      </section>

      {/*
        The works, on the same shelf every other screen uses. A second gallery
        filtered to video would be the library twice, and the library is A6.
      */}
      <section className="flex flex-col gap-4">
        <h2 className="flex items-center gap-2 text-[20px] leading-6 font-bold text-ink">
          <Icon className="text-ink-muted" name="images" size={20} />
          What you have made
        </h2>
        <JobShelf productId={undefined} />
      </section>
    </div>
  );
}

export const Route = createFileRoute('/video-ads')({
  beforeLoad: async ({ location }) => {
    const { data } = await authClient.getSession();
    if (!data) throw redirect({ to: '/login', search: { redirectTo: location.href } });
  },
  component: VideoAds,
});
